import React, { useState } from 'react';

import { useLocation, useHistory } from 'react-router-dom';
import IntlMessages from 'util/IntlMessages';
import BoxContainer from 'components/BoxContainer';
import FilterContainer from 'components/FilterContainer';
import Title from 'components/BoxContainer/components/Title';
import { Form, Input, Button, Row, Col, Divider, notification } from 'antd';

import TitleReport from '../components/TitleReport';
import { useTimeTracker } from '../../contexts/TimeTrackerProvider';
import { useIntl } from 'react-intl';
import { LAYOUT_BY_DEFAULT } from '@src/packages/dashboard/screens/EventsDashboard/constants';
import HashResultTemplate from '../components/HashResultTemplate';
import { getReportDownloadMethods } from '../utils/reportDownloadFactory';
import { KIND_OF_REPORTS } from '../../constants';
import CircleButton from '@src/components/Buttons/CircleButton';
import useCloudFunction from '@src/packages/utils/hooks/clouds/useCloudFunction';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

const GENERAL_INFO_LABELS = ['Información general', 'General Information'];
const EMPLOYER_LABELS = ['Empleador', 'Employer'];
const EMPLOYEE_LABELS = ['Empleado', 'Employee'];

export default function Hash() {
  const { corporation, organizationIds, organization } = useTimeTracker();
  const [form] = Form.useForm();
  const intl = useIntl();
  const location = useLocation();
  const [showResults, setShowResults] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [preparingFile, setPreparingFile] = useState(false);

  const downloadMethods = getReportDownloadMethods(KIND_OF_REPORTS.HASH);
  const { mutateAsync: getAttendanceEvent, loading: getAttendanceEventLoading } = useCloudFunction({
    op: API_OPERATIONS.GET_ATTENDANCE_EVENT,
    runFirstTime: false,
  });
  const history = useHistory();

  const onFinish = async values => {
    const { hash = '' } = values;
    const organizationId = organizationIds[0];
    const isValidHash = hash && hash.length === 64;

    if (!isValidHash) {
      notification.error({
        message: intl.formatMessage({ id: 'work.direction.filter.hash.validation.error' }),
      });
      return;
    }

    if (isValidHash) {
      const { success, data, error } = await getAttendanceEvent({ hash, organizationId });
      if (error) {
        notification.error({ message: intl.formatMessage({ id: 'general.record.not.found' }) });
        return;
      }

      if (success && !data?.templateData?.templateData) {
        notification.error({
          message: intl.formatMessage({ id: 'work.direction.hash.template.not.found' }),
        });
        return;
      }

      if (success) {
        const formattedData = formatResultData(data);
        setResultData(formattedData);
        setShowResults(true);
      }
    } else {
      setResultData(null);
      setShowResults(false);
    }
  };

  const formatResultData = data => {
    const templateData = data?.templateData?.templateData;
    const groups = templateData?.groups || [];

    return {
      title: templateData?.title || '',
      headerText: templateData?.headerText || '',
      dateSentEmail: templateData?.dateSentEmail || '',
      groups,
      description: templateData?.description || '',
      additionalInfo: templateData?.additionalInfo || '',
      dailyReport: templateData?.dailyReport || null,
    };
  };

  const handleBackToSearch = () => {
    setShowResults(false);
    setResultData(null);
    form.resetFields();

    if (location?.search) {
      history.goBack();
    }
  };

  const hashPdfParams = resultData
    ? {
        data: resultData,
        title: resultData.headerText || resultData.title,
        fileName: 'consulta-hash',
        headerText: resultData.headerText,
        dateSentEmail: resultData.dateSentEmail,
        groups: resultData.groups,
        description: resultData.description,
        additionalInfo: resultData.additionalInfo,
        dailyReport: resultData.dailyReport,
      }
    : null;

  const handleDownloadPdf = () => {
    if (!hashPdfParams) return;

    setPreparingFile(true);
    downloadMethods.downloadPdf(hashPdfParams).finally(() => setPreparingFile(false));
  };

  const handlePrintPdf = () => {
    if (!hashPdfParams) return;

    setPreparingFile(true);
    downloadMethods.printPdf(hashPdfParams).finally(() => setPreparingFile(false));
  };

  const hashValidator = (_, value) => {
    if (!value) return Promise.resolve();
    return Promise.resolve();
  };

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hashFromUrl = searchParams.get('value');

    if (hashFromUrl && organizationIds.length > 0) {
      form.setFieldsValue({ hash: hashFromUrl });
      onFinish({ hash: hashFromUrl });
    }
  }, [location.search, organizationIds]);

  return (
    <BoxContainer>
      <BoxContainer content fixed shadow={showResults}>
        <FilterContainer
          titleClassName="gx-w-100"
          goBack={showResults ? handleBackToSearch : null}
          title={
            <Title.Header
              value={
                <div className="gx-flex-row gx-justify-content-between gx-align-items-center">
                  <TitleReport
                    corporationName={corporation?.name || organization?.name}
                    title={
                      <IntlMessages
                        id={
                          showResults ? 'work.direction.hash.result.title' : 'work.direction.hash'
                        }
                      />
                    }
                    rut={corporation?.RUT || organization?.rut}
                  />
                  {showResults && resultData && (
                    <div className="gx-flex-row gx-align-items-center gx-guarnic-gap-2">
                      <CircleButton
                        onClick={handleDownloadPdf}
                        iconName="picture_as_pdf"
                        loading={preparingFile}
                        tooltipText={intl.formatMessage({ id: 'general.download.pdf' })}
                      />
                      <CircleButton
                        onClick={handlePrintPdf}
                        iconName="print"
                        loading={preparingFile}
                        tooltipText={intl.formatMessage({ id: 'general.print' })}
                      />
                    </div>
                  )}
                </div>
              }
            />
          }
        />
      </BoxContainer>
      <BoxContainer content loading={getAttendanceEventLoading}>
        {!showResults ? (
          <Form layout={LAYOUT_BY_DEFAULT} form={form} onFinish={onFinish}>
            <div className="gx-py-3">
              <Title.SubTitle value={<IntlMessages id="work.direction.hash.description" />} />
            </div>
            <Row justify="space-between" gutter={[8, 8]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Title.LabelForm value={<IntlMessages id="work.direction.enter.hash" />} />
                <Form.Item
                  name="hash"
                  rules={[
                    {
                      required: true,
                      message: (
                        <IntlMessages id="work.direction.filter.hash.validation.empty.error" />
                      ),
                    },
                    { validator: hashValidator },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: 'work.direction.enter.hash.placeholder',
                    })}
                    maxLength={64}
                  />
                </Form.Item>
              </Col>
              <div className="gx-flex-column gx-justify-content-center gx-w-100 gx-w-md-25">
                <Button block htmlType="submit" className="gx-mb-0 gx-ml-1" type="primary">
                  <IntlMessages id="button.generate.hash.report" />
                </Button>
              </div>
            </Row>
          </Form>
        ) : (
          <div className="gx-py-4">{resultData && <HashResultTemplate {...resultData} />}</div>
        )}
      </BoxContainer>
    </BoxContainer>
  );
}
